import React from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

// Last-hour temperature graphs for the public landing pages (deviation report and
// alert guide). Same idea as clicking a temp tile on the dashboard.

export interface SensorMeta {
    device_ieee: string;
    device_name: string;
    property: string;
    label: string;
    unit: string;
}

export interface HistoryRow {
    ieee_address: string;
    property: string;
    value_num: number | null;
    value_str: string | null;
    timestamp: string;
}

const timeFmt = (ts: number) => {
    try {
        return new Date(ts).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

const SensorChart: React.FC<{ sensor: SensorMeta; rows: HistoryRow[] }> = ({ sensor, rows }) => {
    const data = rows
        .map(r => ({ ts: new Date(r.timestamp).getTime(), value: r.value_num != null ? Number(r.value_num) : null }))
        .filter(d => !isNaN(d.ts))
        .sort((a, b) => a.ts - b.ts);

    return (
        <div style={{ border: '1px solid #eee', borderRadius: 8, padding: '12px 12px 4px', marginBottom: 16 }}>
            <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#1e3a5f' }}>
                📈 {sensor.device_name} · {sensor.label}
                <span style={{ fontWeight: 400, color: '#888' }}> (senaste timmen)</span>
            </p>
            {data.length === 0 ? (
                <p style={{ color: '#888', fontSize: 13, padding: '16px 0', margin: 0, textAlign: 'center' }}>
                    Ingen data den senaste timmen
                </p>
            ) : (
                <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="ts"
                            type="number"
                            scale="time"
                            domain={['dataMin', 'dataMax']}
                            tickFormatter={ts => timeFmt(ts as number)}
                            tick={{ fontSize: 11 }}
                            minTickGap={40}
                        />
                        <YAxis
                            width={48}
                            tick={{ fontSize: 11 }}
                            unit={sensor.unit ? ` ${sensor.unit}` : undefined}
                        />
                        <Tooltip
                            labelFormatter={ts => timeFmt(ts as number)}
                            formatter={(value: any) => [
                                `${typeof value === 'number' ? value.toLocaleString('sv-SE', { maximumFractionDigits: 2 }) : value}${sensor.unit ? ` ${sensor.unit}` : ''}`,
                                sensor.label,
                            ]}
                            contentStyle={{ fontSize: '12px' }}
                        />
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#f59e0b"
                            dot={data.length < 20 ? { r: 3 } : false}
                            strokeWidth={2}
                            connectNulls
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

/** Renders one last-hour graph per temperature sensor in the rule. Null if none. */
const SensorCharts: React.FC<{ sensors: SensorMeta[]; history: HistoryRow[] }> = ({ sensors, history }) => {
    if (!sensors?.length) return null;
    return (
        <div style={{ marginBottom: 24 }}>
            {sensors.map(s => (
                <SensorChart
                    key={`${s.device_ieee}::${s.property}`}
                    sensor={s}
                    rows={history.filter(h => h.ieee_address === s.device_ieee && h.property === s.property)}
                />
            ))}
        </div>
    );
};

export default SensorCharts;
